import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';

export interface User {
  userId: string;
  email: string;
  name: string;
  role: 'admin' | 'editor';
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresAt: number;
}

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; tokens: AuthTokens } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'TOKEN_REFRESH_SUCCESS'; payload: { tokens: AuthTokens } }
  | { type: 'SET_LOADING'; payload: boolean };

const initialState: AuthState = {
  user: null,
  tokens: null,
  isLoading: true,
  isAuthenticated: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        tokens: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        tokens: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'TOKEN_REFRESH_SUCCESS':
      return {
        ...state,
        tokens: action.payload.tokens,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
}

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token storage keys
const TOKEN_STORAGE_KEY = 'mampd_auth_tokens';
const USER_STORAGE_KEY = 'mampd_user';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load stored authentication data on mount
  useEffect(() => {
    const loadStoredAuth = () => {
      try {
        const storedTokens = localStorage.getItem(TOKEN_STORAGE_KEY);
        const storedUser = localStorage.getItem(USER_STORAGE_KEY);

        if (storedTokens && storedUser) {
          const tokens: AuthTokens = JSON.parse(storedTokens);
          const user: User = JSON.parse(storedUser);

          // Check if tokens are still valid
          if (tokens.expiresAt > Date.now()) {
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: { user, tokens },
            });
          } else {
            // Tokens expired, try to refresh
            refreshTokens(tokens.refreshToken)
              .then((newTokens) => {
                if (newTokens) {
                  dispatch({
                    type: 'TOKEN_REFRESH_SUCCESS',
                    payload: { tokens: newTokens },
                  });
                } else {
                  clearStoredAuth();
                  dispatch({ type: 'LOGIN_FAILURE' });
                }
              })
              .catch(() => {
                clearStoredAuth();
                dispatch({ type: 'LOGIN_FAILURE' });
              });
          }
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        console.error('Error loading stored authentication:', error);
        clearStoredAuth();
        dispatch({ type: 'LOGIN_FAILURE' });
      }
    };

    loadStoredAuth();
  }, []);

  const clearStoredAuth = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  };

  const storeAuth = (user: User, tokens: AuthTokens) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  };

  const login = async (username: string, password: string): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      // Use the configured API URL from environment variables
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `Authentication failed with status ${response.status}`,
        }));
        throw new Error(errorData.message || 'Authentication failed');
      }

      const data = await response.json();
      
      // Parse the ID token to extract user information
      const user = parseUserFromIdToken(data.idToken);
      
      const tokens: AuthTokens = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        idToken: data.idToken,
        expiresAt: Date.now() + (60 * 60 * 1000), // 1 hour from now
      };

      storeAuth(user, tokens);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, tokens },
      });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = () => {
    clearStoredAuth();
    dispatch({ type: 'LOGOUT' });
  };

  const refreshTokens = async (refreshToken: string): Promise<AuthTokens | null> => {
    try {
      // Use the configured API URL from environment variables
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      
      const newTokens: AuthTokens = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || refreshToken, // Some implementations don't rotate refresh tokens
        idToken: data.idToken,
        expiresAt: Date.now() + (60 * 60 * 1000), // 1 hour from now
      };

      return newTokens;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    if (!state.tokens?.refreshToken) {
      return false;
    }

    try {
      const newTokens = await refreshTokens(state.tokens.refreshToken);
      if (newTokens) {
        const updatedTokens = { ...state.tokens, ...newTokens };
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(updatedTokens));
        dispatch({
          type: 'TOKEN_REFRESH_SUCCESS',
          payload: { tokens: updatedTokens },
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  const parseUserFromIdToken = (idToken: string): User => {
    try {
      // In a real implementation, you'd properly decode and verify the JWT
      // For now, we'll create a mock user based on the token
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      
      return {
        userId: payload.sub || 'mock-user-id',
        email: payload.email || 'admin@cloudhesive.com',
        name: payload.name || payload.email?.split('@')[0] || 'Admin User',
        role: payload['custom:role'] || 'admin',
      };
    } catch (error) {
      console.error('Error parsing ID token:', error);
      // Fallback user
      return {
        userId: 'mock-user-id',
        email: 'admin@cloudhesive.com',
        name: 'Admin User',
        role: 'admin',
      };
    }
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};